import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

/** Tells every other copy of the hook on the page that a choice changed. */
const CHANGED = 'remembered-choice-changed';

function read(storageKey: string | null): string | null {
  if (!storageKey) return null;
  try {
    return window.localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

/**
 * A choice a person makes on a screen — which view, which tab — remembered in
 * this browser, per person (`<key>:<userId>`, like `recent-events:<userId>`),
 * so the screen opens where they left it next time. Only a value among
 * `options` is ever answered: a remembered view since removed falls back.
 * Storage that is blocked or full just forgets; the screen works regardless.
 *
 * Every copy of the hook under one key holds one value: a change made through
 * one is announced to the others on the page (and to other tabs, through the
 * `storage` event), so two parts of a screen reading the same choice — the
 * assistant's briefing and its chat — never disagree.
 */
export function useRememberedChoice<T extends string>(
  key: string,
  options: readonly T[],
  fallback: T,
): [T, (next: T) => void] {
  const { user } = useAuth();
  const storageKey = user?.id ? `${key}:${user.id}` : null;
  const [stored, setStored] = useState<{ key: string | null; value: string | null }>(() => ({
    key: storageKey,
    value: read(storageKey),
  }));
  // Another person signing in on this browser reads their own choice.
  const value = stored.key === storageKey ? stored.value : read(storageKey);
  const current = options.includes(value as T) ? (value as T) : fallback;

  useEffect(() => {
    if (!storageKey) return undefined;
    const onChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ key: string; value: string }>).detail;
      if (detail?.key === storageKey) setStored({ key: storageKey, value: detail.value });
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === storageKey) setStored({ key: storageKey, value: event.newValue });
    };
    window.addEventListener(CHANGED, onChanged);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHANGED, onChanged);
      window.removeEventListener('storage', onStorage);
    };
  }, [storageKey]);

  const remember = useCallback(
    (next: T) => {
      setStored({ key: storageKey, value: next });
      if (!storageKey) return;
      try {
        window.localStorage.setItem(storageKey, next);
      } catch {
        // Nothing to do: the choice holds for this visit only.
      }
      // Announced even when storage refused it, so the page still agrees.
      window.dispatchEvent(new CustomEvent(CHANGED, { detail: { key: storageKey, value: next } }));
    },
    [storageKey],
  );
  return [current, remember];
}
